import { createTheme, darken, getContrastRatio, ThemeOptions } from '@mui/material/styles';

// Brand accents also color links, selected navigation, and buttons with white text.
// Darken pale colors for these roles without changing the saved company branding.
const readableAccent = (color: string): string => {
  let accent = color;
  while (getContrastRatio(accent, '#ffffff') < 4.5) {
    accent = darken(accent, 0.05);
  }
  return accent;
};

// Default theme options that can be customized per-account
export const createPortalTheme = (primaryColor: string = '#016ec3', secondaryColor: string = '#0d2137') => {
  const ink = '#2c2a29';

  const themeOptions: ThemeOptions = {
    palette: {
      primary: {
        main: readableAccent(primaryColor),
        contrastText: '#ffffff',
      },
      secondary: {
        main: readableAccent(secondaryColor),
        contrastText: '#ffffff',
      },
      background: {
        default: '#e8f1f8',
        paper: '#ffffff',
      },
      text: {
        primary: ink,
        secondary: 'rgba(44, 42, 41, 0.75)',
      },
      divider: 'rgba(44, 42, 41, 0.08)',
    },
    typography: {
      fontFamily: '"Source Sans 3", "Segoe UI", sans-serif',
      h1: {
        fontFamily: '"DM Sans", "Segoe UI", sans-serif',
        fontWeight: 700,
        fontSize: '2rem',
      },
      h2: {
        fontFamily: '"DM Sans", "Segoe UI", sans-serif',
        fontWeight: 700,
        fontSize: '1.5rem',
      },
      h3: {
        fontFamily: '"DM Sans", "Segoe UI", sans-serif',
        fontWeight: 600,
        fontSize: '1.25rem',
      },
      h4: {
        fontFamily: '"DM Sans", "Segoe UI", sans-serif',
        fontWeight: 600,
        fontSize: '1.125rem',
      },
      h5: {
        fontFamily: '"DM Sans", "Segoe UI", sans-serif',
        fontWeight: 600,
        fontSize: '1rem',
      },
      h6: {
        fontFamily: '"DM Sans", "Segoe UI", sans-serif',
        fontWeight: 600,
        fontSize: '0.875rem',
      },
      button: {
        fontFamily: '"DM Sans", "Segoe UI", sans-serif',
        fontWeight: 600,
        textTransform: 'none',
      },
    },
    shape: {
      borderRadius: 8,
    },
    components: {
      MuiButtonBase: {
        defaultProps: {
          disableRipple: true,
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: 999,
            padding: '0.75rem 1.6rem',
            boxShadow: 'none',
            transition: 'background-color 250ms cubic-bezier(0.4, 0, 0.2, 1), border-color 250ms cubic-bezier(0.4, 0, 0.2, 1)',
          },
          contained: {
            boxShadow: 'none',
            '&:hover': {
              boxShadow: 'none',
            },
          },
          outlined: {
            borderColor: 'rgba(44, 42, 41, 0.12)',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            boxShadow: 'none',
            border: '1px solid rgba(44, 42, 41, 0.08)',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            borderRadius: 10,
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: '#ffffff',
            color: ink,
            borderBottom: '1px solid rgba(44, 42, 41, 0.04)',
            boxShadow: '0 16px 30px rgba(44, 42, 41, 0.04)',
          },
        },
      },
    },
  };

  return createTheme(themeOptions);
};

// Default theme
export const defaultTheme = createPortalTheme();
